import { Button, Checkbox, Drawer, Form, message, Modal, Spin, Table } from "antd";
import { useForm } from "antd/lib/form/Form";
import { ColumnsType } from "antd/lib/table";
import { PenLine, Save, X } from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityResponse,
  RoleActivityResponse,
  SysActivityResponse,
  SysRoleActivityRequest,
  SysRoleActivityService,
  UpdateSysRoleActivityRequest,
} from "../../../../../services/config/qltl-config/sysRoleActivity";
import RoleForm from "./role-form";
import UsersInRole from "./users-in-role";

interface PermissionListProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const PermissionList = ({ open, setOpen }: PermissionListProps) => {
  const [activities, setActivities] = useState<SysActivityResponse[]>([]);
  const [roleActivities, setRoleActivities] = useState<RoleActivityResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [isUpdateLoading, setUpdateLoading] = useState(false);
  const [roleSelected, setRoleSelected] = useState<string | null>(null);

  const isFetched = useRef(false);

  const [form] = useForm();

  const fetchListRoleActivity = async () => {
    setIsLoading(true);

    try {
      const res = await SysRoleActivityService.getListRoleActivities();

      if (res) {
        const activities: SysActivityResponse[] = [];
        const roleActivities: RoleActivityResponse[] = [];

        res.forEach((item) => {
          activities.push(...item.activities);
          roleActivities.push(...item.roleActivities);
        });

        setActivities(activities);
        setRoleActivities(roleActivities);
      }
    } catch (error) {
      message.error((error as Error)?.message || "Lỗi khi lấy dữ liệu");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = useCallback((id: string) => {
    setEditId(id);
  }, []);

  const handleCancel = useCallback(() => {
    setEditId(null);
  }, []);

  const handleSave = useCallback(() => {
    form.submit();
  }, [form]);

  const columns: ColumnsType<RoleActivityResponse> = useMemo(
    () => [
      {
        title: "",
        dataIndex: "roleId",
        key: "roleId",
        fixed: "left",
        width: 110,
        render: (value: string) => {
          return (
            <div className="flex items-center justify-center gap-2">
              {(editId === value && (
                <>
                  <Button
                    style={{
                      color: "#00A9C4",
                    }}
                    type="text"
                    icon={<Save size={16} />}
                    loading={isUpdateLoading}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSave();
                    }}
                  />
                  <Button
                    htmlType="button"
                    type="text"
                    danger
                    icon={<X size={16} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancel();
                    }}
                    disabled={isUpdateLoading}
                  />
                </>
              )) || (
                <Button
                  htmlType="button"
                  style={{
                    color: "#D48806",
                  }}
                  type="text"
                  disabled={isUpdateLoading}
                  icon={<PenLine size={16} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(value);
                  }}
                />
              )}
            </div>
          );
        },
      },
      {
        title: "Vai trò/Cấp truy cập",
        dataIndex: "roleName",
        key: "roleName",
        fixed: "left",
      },
      ...activities.map((activity) => ({
        title: activity.name,
        dataIndex: activity.code,
        key: activity.code,
        width: 150,
        align: "center" as const,
        render: (_: unknown, record: RoleActivityResponse) => {
          if (editId !== record.roleId)
            return (
              <Checkbox
                checked={record.activities.find((item) => item.id === activity.id)?.hasActivity}
                disabled={editId !== record.roleId}
              />
            );

          return (
            <Form.Item name={activity.id} className="!mb-0" valuePropName="checked">
              <Checkbox onClick={(e) => e.stopPropagation()} />
            </Form.Item>
          );
        },
      })),
    ],
    [activities, editId, handleCancel, handleEdit, handleSave, isUpdateLoading],
  );

  const handleFinish = useCallback(
    async (values: Record<string, boolean>) => {
      if (!editId) return;

      try {
        const body: UpdateSysRoleActivityRequest = {
          roleId: editId,
          activities: Object.keys(values)
            .map((key) => (values[key] ? { id: key } : {}))
            .filter((item) => item.id) as SysRoleActivityRequest[],
        };

        await SysRoleActivityService.updateRoleActivities(body);
        await fetchListRoleActivity();

        isFetched.current = true;
        setEditId(null);

        message.success("Cập nhật thành công");
      } catch (error) {
        message.error((error as Error)?.message || "Lỗi khi cập nhật");
      } finally {
        setUpdateLoading(false);
      }
    },
    [editId],
  );

  useEffect(() => {
    if (isFetched.current) return;

    fetchListRoleActivity();
  }, []);

  useEffect(() => {
    if (!editId) return;

    const roleActivity = roleActivities.find((item) => item.roleId === editId);

    form.setFieldsValue(
      roleActivity?.activities.reduce(
        (curr, item) => ({
          ...curr,
          [item.id]: item.hasActivity,
        }),
        {} as Record<string, boolean>,
      ),
    );
  }, [editId, form, roleActivities]);

  return (
    <Spin spinning={isLoading}>
      <Form form={form} onFinish={handleFinish}>
        <Table
          rowKey={(record) => record.roleId || (record as unknown as ActivityResponse).id}
          columns={columns}
          dataSource={roleActivities}
          scroll={{ x: "max-content" }}
          onRow={(record) => ({
            onClick: () => !editId && setRoleSelected(record.roleId),
            className: "cursor-pointer",
          })}
        />
      </Form>

      <Drawer
        rootClassName="z-9999"
        title="Danh sách người dùng"
        closable
        onClose={() => setRoleSelected(null)}
        open={!!roleSelected}
        width={658}
      >
        <UsersInRole open={!!roleSelected} roleId={roleSelected || ""} />
      </Drawer>

      <Modal
        title="Thêm mới vai trò"
        open={open}
        onOk={() => setOpen(false)}
        onCancel={() => setOpen(false)}
        footer={null}
      >
        <RoleForm onClose={() => setOpen(false)} refresh={fetchListRoleActivity} />
      </Modal>
    </Spin>
  );
};

export default memo(PermissionList);
